import { useMemo } from 'react';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import { useDebounce } from '@/shared/lib';
import { useWithdrawCommission as useWithdrawCommissionQuery } from '@/entities/operation';
import type { FiatWithdrawAssetOption } from '@/entities/operation';

interface UseWithdrawCommissionParams {
  amount: number;
  selectedCurrency?: FiatWithdrawAssetOption;
  hasAmountError: boolean;
  onTop?: boolean;
  paymentTypeCode: string;
}

export function useWithdrawCommission({
  amount,
  selectedCurrency,
  hasAmountError,
  onTop = false,
  paymentTypeCode,
}: UseWithdrawCommissionParams) {
  const debouncedAmount = useDebounce(amount, COMMISSION_CHECK_DELAY);

  const commissionEnabled = useMemo(() => {
    if (hasAmountError || !paymentTypeCode) {
      return false;
    }

    const isTyping = amount !== debouncedAmount;
    if (isTyping) {
      return false;
    }

    const amountValue = debouncedAmount;

    if (
      selectedCurrency?.min !== undefined &&
      selectedCurrency?.min > amountValue
    ) {
      return false;
    }

    if (
      selectedCurrency?.available !== undefined &&
      amountValue > selectedCurrency.available
    ) {
      return false;
    }

    const hasValidDebouncedAmount = !!amountValue;
    const hasCurrency = !!selectedCurrency?.code;

    return hasValidDebouncedAmount && hasCurrency;
  }, [
    onTop,
    amount,
    debouncedAmount,
    hasAmountError,
    paymentTypeCode,
    selectedCurrency?.available,
    selectedCurrency?.code,
    selectedCurrency?.min,
  ]);

  const { commissionData, commissionError, isCommissionFetching } =
    useWithdrawCommissionQuery({
      operationType: 'sbp',
      params: {
        currencyCode: selectedCurrency?.code || '',
        amount: debouncedAmount,
        ontop: String(onTop),
        paymentTypeCode,
      },
      enabled: commissionEnabled,
    });

  const isGettingCommission = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    const hasAmount = !!amount;
    const hasCurrency = !!selectedCurrency?.code;
    const isTyping = amount !== debouncedAmount;

    return (isTyping && hasAmount && hasCurrency) || isCommissionFetching;
  }, [
    amount,
    debouncedAmount,
    hasAmountError,
    isCommissionFetching,
    selectedCurrency?.code,
  ]);

  return {
    commissionData,
    commissionError,
    isCommissionFetching,
    isGettingCommission,
  };
}
